import {
  useLocalStorageUpdateCallback,
  useLocalStorageValueCallback,
} from '@aboutyou/storage';
import {useCallback} from 'react';

type AbTestState = 'pending' | 'activated';
interface Experiment {
  name: string;
  value: string;
}

const pendingAbTestsActivations = new Map<string, boolean>();

interface ActivatedAbTest {
  [key: string]: string;
}

const ACTIVATED_AB_TEST_DEFAULT_VALUE = {};

// v2 drops the activations stored under Optimizely. An activation is only
// logged when the stored group differs from the assigned one, so a browser
// that Firebase put back into its old Optimizely group was never logged.
// Tests that kept their Optimizely key had those groups undercounted, e.g.
// ab_missions_claiming ran at 0/50/50 in Optimizely, so only its variants
// were stored and control looked inflated (ASFE-13060).
export const ACTIVATED_AB_TESTS_STORAGE_KEY = 'ay-activated-ab-tests-v2';

const useActivatedAbTestStorage = () => {
  const getActivatedAbTests = useLocalStorageValueCallback<ActivatedAbTest>({
    key: ACTIVATED_AB_TESTS_STORAGE_KEY,
    defaultValue: ACTIVATED_AB_TEST_DEFAULT_VALUE,
  });

  const setActivatedAbTests = useLocalStorageUpdateCallback<ActivatedAbTest>({
    key: ACTIVATED_AB_TESTS_STORAGE_KEY,
  });

  return {
    getActivatedAbTests,
    setActivatedAbTests,
  };
};

export const useActivatedAbTests = () => {
  const {getActivatedAbTests, setActivatedAbTests} =
    useActivatedAbTestStorage();

  const isExperimentActivated = useCallback(
    (experiment: Experiment) => {
      if (pendingAbTestsActivations.get(experiment.name)) {
        return true;
      }
      const activatedAbTests = getActivatedAbTests() as ActivatedAbTest;

      return activatedAbTests?.[experiment.name] === experiment.value;
    },
    [getActivatedAbTests],
  );

  const setTestStateForUserId = useCallback(
    (experiment: Experiment, state: AbTestState) => {
      if (state === 'pending') {
        pendingAbTestsActivations.set(experiment.name, true);
        // We don't want to store the pending state to the local storage, therefor we are bailing early
        return;
      }

      setActivatedAbTests(activatedAbTests => {
        const existingAbTests = activatedAbTests ?? getActivatedAbTests() ?? {};
        return {
          ...existingAbTests,
          [experiment.name]: experiment.value,
        };
      });
    },
    [getActivatedAbTests, setActivatedAbTests],
  );

  return {
    isExperimentActivated,
    setTestStateForUserId,
  };
};
